import type { DesktopModelView, ThinkingLevel } from "./desktop-model.js";
import type { RecommendedThinkingLevel } from './model-thinking.js';

export const RECOMMENDED_STEP_IDS = ["1", "2", "3", "4", "5"] as const;
export type RecommendedStepId = (typeof RECOMMENDED_STEP_IDS)[number];
export type FixedThinkingLevel = Exclude<ThinkingLevel, "auto">;
export interface RecommendedModelStep {
  id: RecommendedStepId;
  catalogModelId: string;
  thinkingLevel: RecommendedThinkingLevel;
  description?: string;
}
export interface RecommendedModelProfileWrite {
  enabled: boolean;
  defaultStepId: "3";
  steps: RecommendedModelStep[];
}
export interface RecommendedModelProfile extends RecommendedModelProfileWrite {
  tenantId: string;
  revision: number;
  updatedAt: string;
  updatedBy: string;
}
export interface RecommendedModelsView {
  revision: number;
  enabled: boolean;
  defaultStepId: "3";
  steps: Array<
    | {
        id: RecommendedStepId;
        available: true;
        model: DesktopModelView;
        thinkingLevel: RecommendedThinkingLevel;
        description?: string;
      }
    | { id: RecommendedStepId; available: false; reason: "not_available" }
  >;
}
export type ModelSelectionIntent =
  | { kind: "recommended"; stepId: RecommendedStepId }
  | { kind: "manual"; modelId: string; thinkingLevel: ThinkingLevel };

export interface RecommendedModelCandidate {
  model: DesktopModelView;
  thinkingLevels: RecommendedThinkingLevel[];
  reasons: string[];
  levelSupport?: Array<{ level: RecommendedThinkingLevel; reasons: string[]; wireValue?: string }>;
  defaultMember?: boolean;
  loginDefault?: boolean;
  billingStatus?: 'unbilled' | 'free' | 'paid' | 'rate-missing';
  thinkingStatus?: 'configured' | 'unconfigured';
}
export interface RecommendedModelProfileView {
  tenantId: string;
  profile: RecommendedModelProfile | undefined;
  candidates: RecommendedModelCandidate[];
  steps: Array<RecommendedModelStep & { reasons: string[] }>;
  status: 'missing' | 'valid' | 'degraded';
}

export const RECOMMENDED_DESCRIPTION_MAX_LENGTH = 160;

export function normalizeRecommendedDescription(value: string): string {
  return value.replace(/\r\n|\r|\n/g, " ").trim();
}

/** Structural validation only. Authorization and provider support belong to the server. */
export function isRecommendedModelProfileWrite(
  value: unknown,
): value is RecommendedModelProfileWrite {
  if (!value || typeof value !== "object") return false;
  const draft = value as Record<string, unknown>;
  if (
    typeof draft.enabled !== "boolean" ||
    draft.defaultStepId !== "3" ||
    !Array.isArray(draft.steps) ||
    draft.steps.length !== 5
  )
    return false;
  const tuples = new Set<string>();
  return Array.from(draft.steps).every((value: unknown, index: number) => {
    if (!value || typeof value !== "object") return false;
    const step = value as Record<string, unknown>;
    if (
      Object.keys(step).some(
        (key) => !["id", "catalogModelId", "thinkingLevel", "description"].includes(key),
      ) ||
      step.id !== RECOMMENDED_STEP_IDS[index] ||
      typeof step.catalogModelId !== "string" ||
      !step.catalogModelId.trim() ||
      typeof step.thinkingLevel !== "string" ||
      !["off", "minimal", "low", "medium", "high", "xhigh", "max", "model-default"].includes(
        step.thinkingLevel,
      ) ||
      (step.description !== undefined &&
        (typeof step.description !== "string" ||
          normalizeRecommendedDescription(step.description).length > RECOMMENDED_DESCRIPTION_MAX_LENGTH))
    )
      return false;
    const tuple = JSON.stringify([step.catalogModelId, step.thinkingLevel]);
    if (tuples.has(tuple)) return false;
    tuples.add(tuple);
    return true;
  });
}
